import { Camera, Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { detectAadhaarQr } from './aadhaarQrPayload';

/**
 * Live camera scanner for the Aadhaar Secure QR.
 *
 * WHY a viewfinder rather than a photo: the API accepts the QR's decimal `payload` directly
 * — the tip in SignCare's own trial UI says as much — and a scanner is what produces that.
 * A single still photo of a laminated Aadhaar card gets one attempt at being decodable, and
 * glare, angle or focus can each waste it. A viewfinder keeps trying every frame while the
 * applicant naturally adjusts, which is why phone QR scanners feel instant.
 *
 * It also removes work rather than adding it: with a payload in hand, the gateway skips
 * image decode entirely, and the face photo still comes back because that photo lives
 * INSIDE the QR payload, not on the card's front.
 *
 * On success it fires `onDetected` once and stops — the caller submits immediately, so the
 * applicant never presses a button. Everything is cleaned up on unmount: the frame loop is
 * cancelled and every camera track stopped, or the phone's camera light stays on.
 */
export function AadhaarQrScanner({
  onDetected,
  onCancel,
}: {
  /** Called ONCE with the digits-only payload. The scanner has already stopped. */
  onDetected: (payload: string) => void;
  onCancel: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Guards against a second detection landing while the caller is already submitting —
  // the loop and the stream both stop asynchronously.
  const doneRef = useRef(false);
  const [status, setStatus] = useState<'starting' | 'scanning' | 'denied' | 'failed'>('starting');

  useEffect(() => {
    let frameTimer: number | undefined;
    let cancelled = false;

    function stopCamera() {
      if (frameTimer !== undefined) window.clearTimeout(frameTimer);
      for (const track of streamRef.current?.getTracks() ?? []) {
        track.stop();
      }
      streamRef.current = null;
    }

    async function start() {
      try {
        // `environment` = rear camera. Not `exact`, so a device with only a front camera
        // still opens one instead of throwing.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        if (cancelled) return;
        setStatus('scanning');
        scan();
      } catch (error) {
        if (cancelled) return;
        // A refused permission is the applicant's decision, not a fault — it gets its own
        // message and the photo route below stays available. Anything else (no camera, a
        // device already using it) reads as "couldn't start".
        const denied =
          error instanceof DOMException &&
          (error.name === 'NotAllowedError' || error.name === 'SecurityError');
        setStatus(denied ? 'denied' : 'failed');
      }
    }

    async function scan() {
      if (cancelled || doneRef.current) return;
      const video = videoRef.current;
      // readyState 2 = HAVE_CURRENT_DATA: there is a frame to look at. Detecting before
      // that throws on some devices.
      if (video && video.readyState >= 2) {
        const payload = await detectAadhaarQr(video);
        if (cancelled || doneRef.current) return;
        if (payload) {
          doneRef.current = true;
          stopCamera();
          onDetected(payload);
          return;
        }
      }

      // ~150ms between attempts rather than every frame: detection is the expensive part
      // and 6-7 looks per second is well past what a hand-held scan needs, while leaving
      // the phone's CPU (and battery) alone.
      frameTimer = window.setTimeout(scan, 150);
    }

    void start();
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [onDetected]);

  // Camera unavailable or refused → hand back to the caller's photo capture, which works
  // everywhere. Said plainly, because "scanner failed" with no way forward is a dead end.
  if (status === 'denied' || status === 'failed') {
    return (
      <div className="flex flex-col gap-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4 text-center">
        <p className="m-0 text-[13px] leading-snug text-[#374151]">
          {status === 'denied'
            ? 'Camera access was blocked, so scanning is not available.'
            : 'The camera could not be started on this device.'}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-2.5 text-sm font-medium text-primary hover:bg-primary/5"
        >
          <Camera size={16} />
          Take a photo instead
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative h-[220px] w-full overflow-hidden rounded-xl bg-black">
        {/* muted + playsInline: a video element that is neither will refuse to autoplay
            inline on mobile and open a fullscreen player instead. */}
        <video
          ref={videoRef}
          muted
          playsInline
          className="h-full w-full object-cover"
          aria-label="Camera preview for scanning the Aadhaar QR"
        />

        {/* Framing guide. Purely visual — detection reads the whole frame, so a QR
            slightly outside the box still scans; the box only tells the applicant where
            to aim, which is what stops them filming the card from across the table. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-[140px] w-[140px] rounded-lg border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
        </div>

        {status === 'starting' && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 text-[13px] text-white">
            <Loader2 size={16} className="animate-spin" />
            Starting camera…
          </div>
        )}
      </div>

      <p className="m-0 text-center text-[12px] leading-snug text-[#6B7280]">
        Point the camera at the QR code on the back of your Aadhaar card. It submits by itself once
        it reads.
      </p>

      <button
        type="button"
        onClick={onCancel}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#E5E7EB] py-2 text-[13px] font-medium text-[#374151] hover:bg-gray-50"
      >
        <X size={15} />
        Take a photo instead
      </button>
    </div>
  );
}
